import { ReactNode } from "react";
import { Box, Skeleton } from "@mui/material";
import { Color, DotLegalChip, DotLegalHeader, DotLegalPaper } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation";
import AiUsageProgressBar from "../aiUsageProgressBar/AiUsageProgressBar";
import { AiUsageProgressBarViewModel } from "../aiUsageProgressBar/AiUsageProgressBar.types";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";

export interface AiUsageBoxProps {
    header: ReactNode;
    info?: string;
    limit: number;
    usage: AiUsageProgressBarViewModel;
    isLoading?: boolean;
    headerMargin?: number;
    footer?: ReactNode;
}

function AiUsageBox(props: AiUsageBoxProps) {
    const { translateString } = useTranslation();
    const { addonsFeatures } = usePlanContext();

    if (props.isLoading) {
        return <Skeleton variant="rounded" height={200} />;
    }

    return (
        <DotLegalPaper>
            <Box sx={{ marginRight: 6, marginLeft: 6 }}>
                <Box sx={{ display: "flex", alignItems: "center", marginBottom: props.headerMargin, marginTop: 2 }}>
                    <Box sx={{ flex: 1 }} />
                    <DotLegalHeader headerStyle="medium" center>
                        <Box sx={{ textTransform: "uppercase" }}>{props.header}</Box>
                    </DotLegalHeader>
                    <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
                        <DotLegalChip
                            value={addonsFeatures.aiFeatures ? translateString("aiFeaturesActive") : translateString("aiFeaturesNotActivated")}
                            color={addonsFeatures.aiFeatures ? undefined : Color.graniteGray}
                            variant="filled"
                            size="small"
                            alternativeColor={addonsFeatures.aiFeatures ? { backgroundColor: "#E6FAEd", color: "#36847D" } : undefined}
                        />
                    </Box>
                </Box>

                {props.info && (
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <DotLegalHeader headerStyle="small" fontWeight={400} marginTop={1}>
                            {props.info}
                        </DotLegalHeader>
                    </Box>
                )}
            </Box>

            <Box sx={{ marginBottom: 3 }}>
                <AiUsageProgressBar limit={props.limit} usage={props.usage} showLegend={addonsFeatures.aiFeatures} />
            </Box>

            {props.footer}
        </DotLegalPaper>
    );
}

export default AiUsageBox;
